import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { ExperienceI18nService } from 'src/app/core/experience/experience-i18n.service';
import { MarkerGoogleMaps } from '../models/maps/markerGoogleMaps';
import { Patient } from '../models/patient';
import { TypeMapMarker } from 'src/app/core/enums/typeMapMarker';
import { Provider } from '../models/provider';
import { ProviderByStatusAndLocation } from 'src/app/core/enums/ProviderByStatusAndLocation';
import { IconMapMarker } from 'src/app/core/enums/iconMapMarker';
import { PATIENT_STATUS } from 'src/app/core/enums/patientStatus';
import { PROVIDER_STATUS } from 'src/app/core/enums/providerStatus';
import { MAP_MARKER_COLORS } from 'src/app/core/theme/theme.facades';

export interface ClusterCountResult {
    total: number;
    byClusterId: Record<number, number>;
}


@Injectable({
    providedIn: 'root'
})

export class MapsService {

    constructor(private experienceI18n: ExperienceI18nService) { }

    private svgMapIcons: { [key: string]: any } = {};
    private apiLoaded = false;
    private maxRetries = 3;
    private retryDelay = 2000;
    private nextClusterId = 1;
    allClusterMembers: MarkerGoogleMaps[] = [];

    public async loadApi(retryCount = 0): Promise<void> {
        if (this.apiLoaded) {
            return Promise.resolve();
        }

        if (window.google?.maps) {
            this.apiLoaded = true;
            return Promise.resolve();
        }

        const existingScript = Array.from(document.getElementsByTagName('script')).find(
            (s) => s.src.includes('maps.googleapis.com/maps/api/js')
        );
        if (existingScript) {

            return new Promise((resolve, reject) => {
                existingScript.addEventListener('load', () => {
                    this.apiLoaded = true;
                    resolve();
                });
                existingScript.addEventListener('error', async () => {
                    if (retryCount < this.maxRetries) {
                        setTimeout(() => {
                            this.loadApi(retryCount + 1).then(resolve).catch(reject);
                        }, this.retryDelay);
                    } else {
                        reject(new Error('No se pudo cargar la API de Google Maps después de varios intentos.'));
                    }
                });
            });
        }

        return new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.googleMapsApiKey}&libraries=places`;
            script.async = true;
            script.defer = true;

            script.onload = () => {
                this.apiLoaded = true;
                resolve();
            };
            script.onerror = async () => {
                console.error(`Intento ${retryCount + 1} fallido al cargar la API de Google Maps`);
                if (retryCount < this.maxRetries) {
                    setTimeout(() => {
                        this.loadApi(retryCount + 1).then(resolve).catch(reject);
                    }, this.retryDelay);
                } else {
                    reject(new Error('No se pudo cargar la API de Google Maps después de varios intentos.'));
                }
            };

            document.head.appendChild(script);
        });
    }

    private geoCodeCache: Map<string, { lat: number, lng: number }> = new Map();

    public async geoCodeAddress(address: string): Promise<{ lat: number, lng: number }> {
        if (this.geoCodeCache.has(address)) {
            return this.geoCodeCache.get(address)!;
        }

        try {
            const coords = await new Promise<{ lat: number, lng: number }>((resolve, reject) => {
                const geocoder = new google.maps.Geocoder();
                geocoder.geocode({ 'address': address }, (results, status) => {
                    if (status === google.maps.GeocoderStatus.OK) {
                        const location = results[0].geometry.location;
                        resolve({ lat: location.lat(), lng: location.lng() });
                    } else {
                        reject(new Error(`Geocode was not successful for the following reason: ${status}`));
                    }
                });
            });

            this.geoCodeCache.set(address, coords);
            return coords;
        } catch (error) {
            throw error;
        }
    }

    getMarkerIcon(icon: IconMapMarker, type?: TypeMapMarker): SVGElement | null {
        const svgIcon = this.getSvgMapIcon(icon);
        if (!svgIcon) {
            console.error(`No se pudo obtener el ícono SVG para ${icon}`);
            return null;
        }

        const clonedSvgIcon = svgIcon.cloneNode(true) as SVGElement;

        if (clonedSvgIcon instanceof SVGElement) {
            clonedSvgIcon.style.setProperty('width', '50px');
            clonedSvgIcon.style.setProperty('height', '50px');
            if (type) {
                clonedSvgIcon.style.setProperty('color', MAP_MARKER_COLORS[type]);
            }
        } else {
            console.warn('El elemento clonado no es un SVGElement válido');
            return null;
        }

        return clonedSvgIcon;
    }

    // public castPatientFromCaseToMarkerGoogleMaps(patient: Patient, type?: TypeMapMarker.PATIENT_ACTIVE | TypeMapMarker.PATIENT_INACTIVE | TypeMapMarker.GENERAL | TypeMapMarker.CASE): MarkerGoogleMaps {
    //     return {
    //         type,
    //         active:patient.active,
    //         name: `${patient.surname}, ${patient.name}`,
    //         phone: patient.phoneNumber,
    //         address: patient?.address?.withoutNumber ? `${patient.address?.street} S/N, ${patient.address?.location?.name}, ${patient.address?.location?.province?.name}` : `${patient.address?.street} ${patient.address?.streetNumber}, ${patient.address?.location?.name}, ${patient.address?.location?.province?.name}`,
    //         email: patient.email,
    //         lat: !!patient.address.latitude ? patient.address.latitude : null,
    //         lng: !!patient.address.longitude ? patient.address.longitude : null,
    //         icon: IconMapMarker[type],
    //         svgIcon: this.getMarkerIcon(IconMapMarker[type]),
    //         id: patient?.id,
    //         oldId: patient.id ?? null,
    //         visible: true,
    //     }
    // }

    public castGenericToMarkerGoogleMaps(patient: Patient, type?: TypeMapMarker.CASE | TypeMapMarker.GENERAL) {
        return {
            type,
            active:patient.active,
            name: `${patient.surname}, ${patient.name}`,
            phone: patient.phoneNumber,
            address: patient?.address?.withoutNumber ? `${patient.address?.street} S/N, ${patient.address?.location?.name}, ${patient.address?.location?.province?.name}` : `${patient.address?.street} ${patient.address?.streetNumber}, ${patient.address?.location?.name}, ${patient.address?.location?.province?.name}`,
            email: patient.email,
            lat: !!patient.address.latitude ? patient.address.latitude : null,
            lng: !!patient.address.longitude ? patient.address.longitude : null,
            id: patient?.id,
            oldId: patient.id ?? null,
            visible: true,
        }
    }

    public castPatientToMarkerGoogleMaps(patient: Patient, type?: TypeMapMarker): MarkerGoogleMaps {
        let address = this.castAddressToMap(patient);
        return {
            type,
            active: patient.active,
            dni: parseInt(patient.documentNumber),
            name: `${patient.surname}, ${patient.name}`,
            phone: patient.phoneNumber,
            address: address.address,
            email: patient.email,
            lat: !!patient.address.latitude ? patient.address.latitude : null,
            lng: !!patient.address.longitude ? patient.address.longitude : null,
            caseId: !!patient.caseId ? patient.caseId : null,
            icon: IconMapMarker[type],
            svgIcon: this.getMarkerIcon(IconMapMarker[type], type),
            id: patient?.id,
            visible: true,
            oldId: patient.id ?? null,
            status: type === TypeMapMarker.PATIENT_ACTIVE ? PATIENT_STATUS.ACTIVE : PATIENT_STATUS.INACTIVE,
        }
    }

    //Convert cuit to dni
    private getDni(value: number): number {
        const string = value.toString();
        const length = string.length;
        const res = string.slice(2, length - 1);
        return parseInt(res);
    }

    public castProvidersToMarkerGoogleMaps(providers: Provider[]): MarkerGoogleMaps[] {
        const markers: MarkerGoogleMaps[] = [];
        if (!!providers) {
            providers.forEach(provider => {
                const address = this.castAddressToMap(provider);
                const type = provider.poiType;
                const marker: MarkerGoogleMaps = {
                    type: provider?.poiType ?? null,
                    active: provider.active,
                    name: `${provider?.surname ?? ''}, ${provider?.name ?? ''}`,
                    phone: provider?.mobilePhoneNumber ? provider.mobilePhoneNumber : provider?.phoneNumber ? provider.phoneNumber : null,
                    address: address.address,
                    email: provider?.email ?? null,
                    observations: provider?.observations?.trim() || null,
                    dni: this.getDni(provider?.cuit ?? 0),
                    specialties: provider?.specialties ?? [],
                    lat: !!provider.address?.latitude ? provider.address.latitude : null,
                    lng: !!provider.address?.longitude ? provider.address.longitude : null,
                    icon: IconMapMarker[type],
                    svgIcon: this.getMarkerIcon(IconMapMarker[type], type),
                    id: provider?.id ?? null,
                    visible: true,
                    oldId: provider.oldId ?? null,
                    status: provider?.status ?? null
                };
                markers.push(marker);
            });
        }
        return markers;
    }

    public castAddressToMap(entity?: Patient | Provider): { address: string } {
        const addr = entity?.address;
        const street = addr?.street ? `${addr.street} ` : '';
        const streetNumber = addr?.withoutNumber === true || addr?.streetNumber == null ? 'S/N ' : `${addr.streetNumber} `;
        const locationName = addr?.location?.name ? `${addr.location.name} ` : '';
        const provinceName = addr?.location?.province?.name ? `${addr.location.province.name} ` : '';
        const parts = [street, streetNumber, locationName, provinceName].filter(part => part.trim().length > 0).join(', ').trim();
        const addressString = parts || this.experienceI18n.translate('core.services.maps.sin-direccion-disponible', undefined, 'Dirección no disponible');

        return { address: addressString };
    }


    isApiLoaded(): boolean {
        return this.apiLoaded
    }

    public preloadMapIcons(): Promise<void> {
        ``
        const timestamp = new Date().getTime();

        const iconUrls = Object.values(IconMapMarker).map(key => ({
            key,
            url: `assets/icons/${key}.svg?t=${timestamp}`
        }));

        const iconPromises = iconUrls.map(icon =>
            fetch(icon.url)
                .then(response => response.text())
                .then(svgString => {
                    const parser = new DOMParser();
                    const svgElement = parser.parseFromString(svgString, 'image/svg+xml').documentElement;
                    this.svgMapIcons[icon.key] = svgElement;
                })
                .catch(error => {
                    console.error(`Error loading icon ${icon.key}:`, error);
                })
        );

        return Promise.all(iconPromises).then(() => { });
    }

    // get prelaoaded SVG icon by key
    public getSvgMapIcon(iconKey: string): SVGElement | null {
        return this.svgMapIcons[iconKey] || null;
    }

    public matchProvidersStatusAndLocation(search: string): ProviderByStatusAndLocation[] {
        let filters = (Object.values(ProviderByStatusAndLocation) as string[])
        let filtered = filters.filter(val => val.includes(search)).map(val => val as ProviderByStatusAndLocation);
        return filtered;
    }

    public providersStatusLocationsToMarkers(statusKeys: ProviderByStatusAndLocation[]): TypeMapMarker[] {
        if (!!statusKeys) {
            return statusKeys.map((statusKey) => {
                // build the enum key from the statusKey
                const enumKey = `PROVIDER_${statusKey}` as keyof typeof TypeMapMarker;
                //get the marker from TypeMapMarker using the enum key
                const marker = TypeMapMarker[enumKey];
                if (marker === undefined) {
                    throw new Error(`No existe el marcador para ProviderByStatusAndLocation.${statusKey}`);
                }
                return marker;
            });
        }
    }

    public extractStatuses(inputs: ProviderByStatusAndLocation[],): PROVIDER_STATUS[] {
        const uniques = Array.from(
            new Set(
                inputs.map(s => s.split('_')[0] as keyof typeof PROVIDER_STATUS)
            )
        );
        return uniques.map(key => PROVIDER_STATUS[key]);
    }


    public filterAndCluster(
        markers: MarkerGoogleMaps[],
        clusterMembers: MarkerGoogleMaps[] = []
    ): {
        updatedMarkers: MarkerGoogleMaps[];
        clusters: MarkerGoogleMaps[];
        clustersMembers: MarkerGoogleMaps[];
    } {

        const coordKey = (lat?: number, lng?: number) =>
            lat == null || lng == null ? '' : `${lat.toFixed(6)},${lng.toFixed(6)}`;

        /* ---------- filter duplicates--------- */
        const dedupeMarkers = (markers: MarkerGoogleMaps[]) => {
            const seen = new Set<number>();
            return markers?.filter(m => {
                if (m.id == null) return true;
                if (seen.has(m.id)) return false;
                seen.add(m.id);
                return true;
            });
        };

        const currentMarkers = dedupeMarkers(markers);
        const currentMembers = dedupeMarkers(clusterMembers);

        /* ---------- build actual clusters index ---------- */
        interface Bucket {
            clusterId: number;
            clusterMarker: MarkerGoogleMaps;
            members: MarkerGoogleMaps[];
        }

        const clusterBuckets = new Map<string, Bucket>();

        // red pois.type === CLUSTER
        if (currentMarkers.length === 0) {
            return;
        }
        for (const m of currentMarkers) {
            if (m.type !== TypeMapMarker.CLUSTER) continue;
            const key = coordKey(m.lat, m.lng);
            if (!key) continue;

            clusterBuckets.set(key, {
                clusterId: m.clusterId ?? m.id, // fallback
                clusterMarker: m,
                members: []
            });
        }

        // read previpus clustersMembers
        for (const cm of currentMembers) {
            const key = coordKey(cm.lat, cm.lng);
            if (!key) continue;


            const bucket = clusterBuckets.get(key) ?? (() => {
                const fakeCluster: MarkerGoogleMaps = {
                    ...cm,
                    type: TypeMapMarker.CLUSTER,
                    id: cm.clusterId!,            // same id
                    clusterId: cm.clusterId!,
                    clusterSize: 0,               //update later
                    icon: IconMapMarker[TypeMapMarker.CLUSTER],
                    svgIcon: this.getMarkerIcon(IconMapMarker[TypeMapMarker.CLUSTER], TypeMapMarker.CLUSTER),
                    visible: true
                };
                const b: Bucket = { clusterId: cm.clusterId!, clusterMarker: fakeCluster, members: [] };
                clusterBuckets.set(key, b);
                return b;
            })();

            bucket.members.push(cm);
        }

        const markersToKeep: MarkerGoogleMaps[] = [];
        const newMembers: MarkerGoogleMaps[] = [];

        for (const m of currentMarkers) {
            if (m.type === TypeMapMarker.CLUSTER) {
                markersToKeep.push(m);   // is cluster
                continue;
            }
            const key = coordKey(m.lat, m.lng);
            const bucket = key ? clusterBuckets.get(key) : undefined;

            if (bucket) {
                //if there is a cluster at those coordinates
                const member = { ...m, clusterId: bucket.clusterId };
                bucket.members.push(member);
                newMembers.push(member);
            } else {
                markersToKeep.push(m);
            }
        }

        /* ---------- Build New Clusters ---------- */
        const groups = new Map<string, MarkerGoogleMaps[]>();

        for (const m of markersToKeep) {
            if (m.type === TypeMapMarker.CLUSTER) continue; // ignore clusters
            const key = coordKey(m.lat, m.lng);
            if (!key) continue;
            const g = groups.get(key) ?? [];
            m.oldId = m.oldId;
            g.push(m);
            groups.set(key, g);
        }

        const newClusters: MarkerGoogleMaps[] = [];

        for (const [key, group] of groups) {
            if (group.length < 2) continue;              //cluster min.
            group.forEach(gm => {
                const idx = markersToKeep.indexOf(gm);
                if (idx >= 0) markersToKeep.splice(idx, 1);
            });

            //Build unique ClusterID
            const cid = this.nextClusterId++;
            const [latStr, lngStr] = key.split(',');
            const lat = +latStr, lng = +lngStr;

            const clusterMarker: MarkerGoogleMaps = {
                type: TypeMapMarker.CLUSTER,
                id: cid,
                clusterId: cid,
                lat, lng,
                visible: true,
                status: group[0].status,
                clusterSize: group.length,
                icon: IconMapMarker[TypeMapMarker.CLUSTER],
                svgIcon: this.getMarkerIcon(IconMapMarker[TypeMapMarker.CLUSTER], TypeMapMarker.CLUSTER),
            };

            newClusters.push(clusterMarker);
            clusterBuckets.set(key, { clusterId: cid, clusterMarker, members: [] });

            group.forEach(m => {
                const member = { ...m, clusterId: cid };
                newMembers.push(member);
                clusterBuckets.get(key)!.members.push(member);
            });
        }

        /* ---------- Update ClustersSizes--------- */
        for (const { clusterMarker, members } of clusterBuckets.values()) {
            clusterMarker.clusterSize = members.length;
        }

        /* ---------- Return ---------- */
        const allClusterMembers = [...currentMembers, ...newMembers]; // actualizados
        const updatedMarkers = [...markersToKeep, ...newClusters];

        return {
            updatedMarkers,
            clusters: newClusters,
            clustersMembers: allClusterMembers
        };
    }
}
